'use client';

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthenticatedApi } from '@/hooks/useAuthenticatedApi';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@clerk/clerk-react';
import { MessageSquarePlus, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import LoadingSpinner from '@/components/LoadingSpinner';
import ChatListItem from '@/components/chat/ChatListItem';
import EmptyChatsState from '@/components/chat/EmptyChatsState';
import DeleteChatDialog from '@/components/chat/DeleteChatDialog';
import type { AIChat } from '@/types/chat';

export default function AIChats() {
  const { isLoaded, isSignedIn } = useAuth();
  const { apiCall } = useAuthenticatedApi();
  const { toast } = useToast();
  const navigate = useNavigate();

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [chats, setChats] = useState<AIChat[]>([]);
  const [lastKey, setLastKey] = useState<string | undefined>(undefined);
  const [hasMore, setHasMore] = useState(false);
  const [isDeleting, setIsDeleting] = useState<Record<string, boolean>>({});
  const [chatToDelete, setChatToDelete] = useState<AIChat | null>(null);

  // Authentication check
  useEffect(() => {
    if (isLoaded && !isSignedIn) {
      navigate('/signin');
    }
  }, [isLoaded, isSignedIn, navigate]);

  // Fetch chats
  const fetchChats = useCallback(
    async (lastKeyParam?: string, append = false) => {
      const loadingState = append ? setIsLoadingMore : setIsLoading;
      loadingState(true);

      try {
        const endpoint = lastKeyParam ? `/ai/chats?last=${lastKeyParam}` : '/ai/chats';
        const response = await apiCall<AIChat[]>(endpoint, 'GET');

        if (response && Array.isArray(response)) {
          // In a real implementation, we'd handle the lastKey from the response
          // For now, we'll simulate it based on the array length
          const newChats = response;

          setChats((prev) => (append ? [...prev, ...newChats] : newChats));

          // Simulate lastKey logic - in real implementation this would come from the API
          const simulatedHasMore = newChats.length >= 10; // Assuming page size of 10
          setHasMore(simulatedHasMore);

          if (simulatedHasMore && newChats.length > 0) {
            setLastKey(newChats[newChats.length - 1].id);
          } else {
            setLastKey(undefined);
          }
        } else {
          if (!append) {
            setChats([]);
            setHasMore(false);
            setLastKey(undefined);
          }
        }
      } catch (error) {
        console.error('Error fetching AI chats:', error);
        toast({
          title: 'Error',
          description: 'Failed to load AI chats. Please try again.',
          variant: 'destructive',
        });
      } finally {
        loadingState(false);
      }
    },
    [apiCall, toast]
  );

  // Initial fetch
  useEffect(() => {
    if (isSignedIn) {
      fetchChats();
    }
  }, [isSignedIn, fetchChats]);

  // Delete chat handlers
  const confirmDelete = (chat: AIChat) => {
    setChatToDelete(chat);
  };

  const cancelDelete = () => {
    setChatToDelete(null);
  };

  // Add this new function for direct deletion
  const handleDirectDelete = async (chat: AIChat) => {
    const id = chat.id;
    setIsDeleting((prev) => ({ ...prev, [id]: true }));

    try {
      await apiCall(`/ai/chat?id=${id}`, 'DELETE');
      setChats(chats.filter((c) => c.id !== id));

      toast({
        title: 'Success',
        description: 'AI chat deleted successfully',
      });
    } catch (error) {
      console.error('Error deleting AI chat:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete AI chat. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsDeleting((prev) => ({ ...prev, [id]: false }));
    }
  };

  const handleDeleteChat = async () => {
    if (!chatToDelete) return;

    const id = chatToDelete.id;
    setIsDeleting((prev) => ({ ...prev, [id]: true }));

    try {
      await apiCall(`/ai/chat?id=${id}`, 'DELETE');
      setChats(chats.filter((chat) => chat.id !== id));

      toast({
        title: 'Success',
        description: 'AI chat deleted successfully',
      });
    } catch (error) {
      console.error('Error deleting AI chat:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete AI chat. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsDeleting((prev) => ({ ...prev, [id]: false }));
      setChatToDelete(null);
    }
  };

  // Navigation
  const handleNewChat = () => {
    navigate('/ai-chat');
  };

  // Loading state
  if (!isLoaded || !isSignedIn) {
    return <LoadingSpinner />;
  }

  return (
    <div className="container mx-auto py-6 px-4 md:px-6">
      <div className="flex flex-col gap-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">AI Chats</h1>
            <p className="text-muted-foreground">View and manage your AI conversations</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() => fetchChats()}
              disabled={isLoading}
              aria-label="Refresh chats"
            >
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
            <Button onClick={handleNewChat} className="ml-auto">
              <MessageSquarePlus className="h-4 w-4 mr-2" />
              New Chat
            </Button>
          </div>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <LoadingSpinner />
          </div>
        ) : chats.length === 0 ? (
          <EmptyChatsState onNewChat={handleNewChat} />
        ) : (
          <>
            <div className="flex flex-col gap-4">
              {chats.map((chat) => (
                <ChatListItem
                  key={chat.id}
                  chat={chat}
                  onDeleteClick={confirmDelete}
                  onShiftDelete={handleDirectDelete}
                  isDeleting={!!isDeleting[chat.id]}
                />
              ))}
            </div>

            {/* Load more button */}
            {hasMore && (
              <div className="flex justify-center mt-4">
                <Button variant="outline" onClick={() => fetchChats(lastKey, true)} disabled={isLoadingMore}>
                  {isLoadingMore ? (
                    <>
                      <div className="h-4 w-4 mr-2">
                        <LoadingSpinner />
                      </div>
                      Loading more...
                    </>
                  ) : (
                    'Load more'
                  )}
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete confirmation dialog */}
      <DeleteChatDialog
        chat={chatToDelete}
        isDeleting={chatToDelete ? !!isDeleting[chatToDelete.id] : false}
        onClose={cancelDelete}
        onConfirm={handleDeleteChat}
      />
    </div>
  );
}
